import { Button, LazyLoader } from '@bp/ui-components';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { AboutModal } from 'components/Modals/AboutModal/AboutModal';
import type React from 'react';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { PageLayoutPlaceholder } from './components/PageLayoutPlaceholder/PageLayoutPlaceholder';
import { updateClaims } from './utils/authStore';
import { bpUserManager } from './utils/userManager';

const stateSchema = object({ returnUrl: string().optional() });

type RequireAuthProps = {
  children?: React.ReactNode;
};

export const RequireAuth: FC<RequireAuthProps> = ({ children }) => {
  const auth = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [showAbout, setShowAbout] = useState<boolean>(false);
  const [isUserManagerReady, setIsUserManagerReady] = useState(false);
  const [hasTriedSigning, setHasTriedSigning] = useState(false);

  // Check if UserManager is properly initialized and synced with session
  useEffect(() => {
    const checkUserManager = async () => {
      const userManager = bpUserManager.getUserManager();
      if (!userManager) {
        return;
      }

      try {
        const user = await userManager.getUser();
        if (!user && auth.isAuthenticated) {
          // We have auth data but UserManager isn't aware, try to sync
          await userManager.signinSilent();
        }
        setIsUserManagerReady(true);
      } catch (error) {
        console.error('Error syncing UserManager:', error);
        // If silent sign-in fails, we'll fall back to regular auth flow
        setIsUserManagerReady(true);
      }
    };

    if (auth.isAuthenticated && !isUserManagerReady) {
      void checkUserManager();
    }
  }, [auth.isAuthenticated, isUserManagerReady]);

  useEffect(() => {
    // Handle the 'appUrlOpen' event and call `handleRedirectCallback`
    void CapApp.addListener('appUrlOpen', async ({ url }) => {
      if (url.includes('state') && (url.includes('code') || url.includes('error'))) {
        const user = await bpUserManager.getUserManager().signinCallback(url);
        await bpUserManager.getUserManager().storeUser(user ?? null);
        await Browser.close();
      }
    });
  }, []);

  const params = new URLSearchParams(location.search);
  const accessDenied = params.get('error') === 'access_denied';

  useEffect(() => {
    if (auth.isAuthenticated && isUserManagerReady) {
      const redirectAfterSignIn = async () => {
        try {
          const { returnUrl } = await stateSchema.validate(auth.user?.state);
          if (returnUrl && returnUrl !== '/login') {
            navigate(returnUrl);
          }
        } catch (error) {
          console.info('Redirect after SignIn was not possible, because no returnUrl was provided');
          console.log(error);
        }
      };
      void redirectAfterSignIn();
    }
  }, [auth.isAuthenticated, isUserManagerReady, navigate, auth.user?.state]);

  useEffect(() => {
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSigning) {
      const returnUrl = location.pathname + location.search;
      const state = { returnUrl };
      void auth.signinRedirect({ state });
      setHasTriedSigning(true);
    }
  }, [auth, hasTriedSigning, location]);

  // TODO: find reasonable interval
  const autoUpdateInterval = 10_000;
  const autoUpdateClaims = useCallback(() => {
    if (auth.isAuthenticated && isUserManagerReady) {
      void updateClaims();
    }
  }, [auth, isUserManagerReady]);

  useEffect(() => {
    const id = setInterval(() => autoUpdateClaims(), autoUpdateInterval);
    return () => clearInterval(id);
  }, [autoUpdateClaims]);

  if (auth.isLoading || (auth.isAuthenticated && !isUserManagerReady)) {
    return (
      <PageLayoutPlaceholder>
        <LazyLoader embedded />
      </PageLayoutPlaceholder>
    );
  }

  if (auth.error) {
    return (
      <PageLayoutPlaceholder alignCenter>
        <div className='mb-6'>
          {auth.error.message} ({auth.error.name})
        </div>
        <Button hierarchy='primary' onClick={() => auth.signinRedirect()} fullWidth>
          {t('auth.login')}
        </Button>
      </PageLayoutPlaceholder>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <PageLayoutPlaceholder alignCenter>
        {!accessDenied && (
          <>
            <Button hierarchy='primary' onClick={() => auth.signinRedirect()} fullWidth className='mt-4'>
              {t('auth.login')}
            </Button>
            <Button hierarchy='tertiary' onClick={() => setShowAbout(true)} fullWidth>
              {t('common.about')}
            </Button>
          </>
        )}
        {accessDenied && (
          <>
            <div className='mb-5'>{params.get('error_description') ?? t('auth.error')}</div>
            <div className='mb-4'>{t('auth.errorDescription')}</div>
            <div className='mb-7'>{t('auth.errorHint')}</div>
            <Button hierarchy='primary' onClick={() => auth.signoutRedirect()} fullWidth>
              {t('auth.logout')}
            </Button>
          </>
        )}

        <AboutModal state={showAbout} setState={(value) => setShowAbout(value)} />
      </PageLayoutPlaceholder>
    );
  }

  return <>{children}</>;
};
